import type { Mark } from './transcript';

/**
 * One line of the on-screen conversation.
 *
 * Structured rather than `string[]`: a mark belongs to one learner turn, and
 * matching it back to a prefixed string would mean parsing our own output.
 * Speaker prefixes are added when the line is rendered, not stored here — the
 * same line has to read as "You:" in one register and be hidden in the other.
 */
export type Line =
  // `translationTarget` is what `translateTurn` will put this line into --
  // the server's own answer (`Turn.translationTarget`, or the matching field
  // on the live commit event), not something computed here. `aidsFor` reads
  // it to decide whether a learner line offers §5's "Say this in English",
  // and `AidsPanel` reads it for the same reason on either speaker's line
  // (its "Translate into …" button and `Turn.translation(language:)` query
  // both need the language the server will actually use). Absent from the
  // `error` variant: an error line is not speech at all, so there is nothing
  // to translate.
  | { who: 'learner'; turnId: string; text: string; translationTarget: string | null }
  // Carries a turn id for the same reason the learner variant does: the aids
  // panel (`aids.ts`, `AidsPanel.tsx`) offers `translateTurn` and `speak` on
  // either speaker's line, and both mutations take the turn's id.
  | { who: 'tutor'; turnId: string; text: string; translationTarget: string | null }
  | { who: 'error'; text: string };

/**
 * A persisted turn, as `Session.turns` carries it.
 *
 * Kept free of Relay's generated types for the same reason `sessions.ts` and
 * `debrief.ts` are: this is the testable seam, and a plain `node --test` run
 * has no Relay runtime.
 */
export type PriorTurn = {
  id: string;
  speaker: string;
  transcript: string;
  translationTarget: string | null;
  errors: readonly PriorError[];
};

export type PriorError = {
  id: string;
  learnerForm: string;
  targetForm: string;
  span?: { start: number; end: number } | null;
};

/**
 * Replay a session's stored turns as the lines and marks the live subscription
 * would have produced.
 *
 * This is what makes resume mean anything: a learner who reloads mid-conversation
 * has said things the socket will never re-announce, because the event bus is a
 * broadcast with no replay (see `events.rs`). The rows are the only record, and
 * they are already there.
 *
 * `span` is nullable on the wire — an extraction that found an error but could
 * not locate it in the transcript. Those turns keep their line and lose only the
 * underline, because `markUp` has nothing to place. Dropping the line instead
 * would silently delete something the learner actually said.
 *
 * Tutor turns carry no errors by construction (only learner speech is analysed),
 * so their errors are ignored rather than trusted.
 */
export function linesFromTurns(turns: readonly PriorTurn[]): {
  lines: Line[];
  marks: Record<string, Mark[]>;
} {
  const lines: Line[] = [];
  const marks: Record<string, Mark[]> = {};
  for (const turn of turns) {
    if (turn.speaker === 'LEARNER') {
      lines.push({
        who: 'learner',
        turnId: turn.id,
        text: turn.transcript,
        translationTarget: turn.translationTarget,
      });
      const located = turn.errors
        .filter((e) => e.span != null)
        .map((e) => ({
          id: e.id,
          start: e.span!.start,
          end: e.span!.end,
          learnerForm: e.learnerForm,
          targetForm: e.targetForm,
        }));
      if (located.length > 0) marks[turn.id] = located;
    } else {
      lines.push({
        who: 'tutor',
        turnId: turn.id,
        text: turn.transcript,
        translationTarget: turn.translationTarget,
      });
    }
  }
  return { lines, marks };
}

/**
 * How many turns the learner took.
 *
 * The condition the server enqueues a debrief on (`Session::has_learner_turns`),
 * restated where the debrief pane needs it. Tutor lines do not count -- the
 * tutor opens every conversation, so counting its greeting would make a session
 * nobody answered look like one worth reviewing, and the pane would wait on a
 * job that was never queued. Error lines are this screen's own messages rather
 * than turns.
 */
export function learnerTurnCount(lines: readonly Line[]): number {
  return lines.filter((l) => l.who === 'learner').length;
}

/**
 * Whether this screen should open on "the tutor is about to speak".
 *
 * `Session::has_any_turns` asked from the other side: a session with nothing
 * in it is one the server is greeting *right now*, because the socket gate it
 * greets on is that same question (`audio_ws/mod.rs`, PRD §3). Any turn at all
 * means the conversation has started, greeting included -- which is why this
 * is not `learnerTurnCount`: a session where only the tutor has spoken has
 * been greeted already and must not be shown waiting for a second hello.
 *
 * Asked of the replayed turns only. The lines this mount has heard live are
 * not evidence about a greeting that was decided before it subscribed.
 */
export function opensThinking(replayed: readonly Line[]): boolean {
  return replayed.length === 0;
}

/**
 * Whether the greeting `opensThinking` waits on has reached the learner, so
 * the talk button may be pressed.
 *
 * Latched by the caller: `thinking` with no reply recurs on every later turn,
 * where it means "composing", not "not ready". The first word is enough --
 * the models are loaded by then, and talking over the greeting is barge-in.
 * `idle` counts too: a failed greeting ends there, and a button still
 * disabled after it would leave the learner no way to start.
 *
 * A press during the cold load was the usual way a learner sent a clip of
 * silence, which Whisper answers with words nobody said.
 */
export function greeted({ state, reply }: { state: string; reply: string }): boolean {
  return state !== 'thinking' || reply !== '';
}
